/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { supabase } from './supabase';

// ── System prompts (single source of truth — quoted in the plan summary for review) ────────
const REACTIVE_SYSTEM = `You are a menu consultant helping a restaurant owner adapt when an ingredient's price spikes.

INPUT CONTEXT:
- restaurant_type: the service format (e.g. "buffet", "fine_dining", "quick_service", or free-form). Suggestions MUST suit this format — see Service Format Constraints below.
- flagged_ingredient: the ingredient whose price jumped, with its % increase over the last 30 days.
- affected_dish: the current menu item that uses it, with its current margin.
- available_ingredients: a deduplicated list of EVERY ingredient the restaurant has purchased in the last 90 days. Each entry has a normalized name and an approximate cost per unit. THIS LIST IS EXHAUSTIVE. If an ingredient is not in this list, you MUST NOT use it.

SERVICE FORMAT CONSTRAINTS (check EACH suggestion against these before including it):
- buffet / catering: dishes must hold heat or cold for 1–2 hours without degrading. Favor braises, stews, casseroles, pasta bakes, roasted proteins in jus, rice/grain bowls, composed cold salads, carving stations. AVOID fried items (go soggy in warmers), pre-assembled sandwiches/tacos/sliders (get soft), à-la-minute plated dishes, delicate seared items.
- fine_dining: plated, refined, composed. Expect precise portions and higher price tier. Avoid "bar food" staples.
- quick_service / fast_casual: fast to assemble, hand-held or single-bowl, crave-able, price-competitive. Nothing requiring >5 min finish time.
- food_truck: hand-held, limited prep surface, minimal station count. Avoid anything needing multiple simultaneous hot pans.
- bar_gastropub / full_service: most flexible — pub classics, shared plates, comfort food, cocktails all fair game.
- cafe_bakery: breakfast, pastries, light lunches, beverages. Avoid heavy dinner entrees.
- catering: similar to buffet — must survive transport + holding.
If restaurant_type is missing or free-form, infer the closest match and still apply format suitability.

TASK:
Suggest 2–3 alternative dishes that could replace or supplement the affected menu item, using ONLY ingredients from available_ingredients AND ONLY dishes that suit the restaurant_type.

HARD CONSTRAINTS:
1. Every ingredient you name MUST appear in available_ingredients — exactly match one of the normalized names.
2. Do not invent substitute ingredients, specialty items, imports, or anything the owner hasn't bought recently.
3. Each suggestion must suit the Service Format Constraints above. Do not include a dish that would degrade in the stated service format.
4. estimatedFoodCost must be computed from the per-unit costs provided in available_ingredients.
5. estimatedMargin is (suggestedPrice − estimatedFoodCost) assuming a sensible menu price that matches the restaurant's existing price tier.

OUTPUT:
Return valid JSON only — no markdown, no preamble, no trailing commentary.
Exact shape:
{
  "suggestions": [
    {
      "dishName": "string",
      "description": "1–2 sentence customer-facing description",
      "ingredients": ["string", ...],
      "estimatedFoodCost": number,
      "estimatedMargin": number,
      "reasoning": "why this suggestion addresses the price spike"
    }
  ]
}

If you cannot generate grounded suggestions, return { "suggestions": [] }. Do not fabricate.`;

const ONDEMAND_SYSTEM = `You are a creative menu consultant helping a restaurant owner design specials.

INPUT CONTEXT:
- restaurant_type: the service format (e.g. "buffet", "fine_dining", "quick_service", or free-form). Specials MUST suit this format — see Service Format Constraints below.
- user_request: what kind of specials the owner wants.
- category: 'food', 'bar', or 'both'.
- occasion: optional — event, holiday, game day, season (may be empty).
- current_date and season: for thematic alignment.
- current_menu: a short list of existing menu items with prices, to match the restaurant's style and price tier.
- available_ingredients: a deduplicated list of EVERY ingredient the restaurant has purchased in the last 90 days. THIS LIST IS EXHAUSTIVE. If an ingredient is not in this list, you MUST NOT use it.

SERVICE FORMAT CONSTRAINTS (check EACH special against these before including it):
- buffet / catering: dishes must hold heat or cold for 1–2 hours without degrading. Favor braises, stews, casseroles, pasta bakes, roasted proteins in jus, rice/grain bowls, composed cold salads, carving stations. AVOID fried items, pre-assembled sandwiches/tacos/sliders, à-la-minute plated dishes, delicate seared items.
- fine_dining: plated, refined, composed. Expect precise portions and higher price tier.
- quick_service / fast_casual: fast to assemble, hand-held or single-bowl, crave-able.
- food_truck: hand-held, limited prep surface, minimal station count.
- bar_gastropub / full_service: pub classics, shared plates, comfort food, cocktails all fair game.
- cafe_bakery: breakfast, pastries, light lunches, beverages.
If restaurant_type is missing or free-form, infer the closest match and still apply format suitability.

TASK:
Generate exactly 5 specials matching the owner's request AND the restaurant_type. Match the style and price tier of current_menu. Think like a chef working within what's in the walk-in AND what will actually survive the service format.

HARD CONSTRAINTS:
1. Every ingredient you name MUST appear in available_ingredients — exactly match one of the normalized names.
2. Do not invent ingredients, substitute items, or anything the owner hasn't bought recently.
3. Each special must suit the Service Format Constraints above. Do not include a dish that would degrade in the stated service format (e.g. fried tacos in a buffet).
4. estimatedCost must be computed from the per-unit costs provided in available_ingredients.
5. suggestedPrice should align with the price tier of current_menu.
6. estimatedMargin = suggestedPrice − estimatedCost.

OUTPUT:
Return valid JSON only — no markdown, no preamble, no trailing commentary.
Exact shape:
{
  "specials": [
    {
      "name": "string",
      "description": "string",
      "ingredients": ["string", ...],
      "estimatedCost": number,
      "suggestedPrice": number,
      "estimatedMargin": number,
      "reasoning": "why this fits the request + occasion"
    }
  ]
}

If you cannot generate 5 grounded specials, return fewer — do not fabricate to hit the count.`;

// ── Helpers ─────────────────────────────────────────────────────────────────────────────────
const MODEL = 'claude-sonnet-4-5';

// Strip packing/case noise from an ingredient name for display ("Yellow Onions (50 lb cs)" →
// "Yellow Onions"). Grounding validation still runs against the original names, so this only
// affects what shows on chips/print cards.
function cleanIngredientName(s) {
  if (!s) return '';
  let out = String(s);
  out = out.replace(/\s*\([^)]*\)/g, '');                  // drop parenthesized packing info
  out = out.replace(/\s*-\s*(case|cs|ea|each|pack|ct|lb|oz)\.?\s*$/i, ''); // trailing "- case" etc
  out = out.replace(/\s+\d+\/\d+\s+(case|cs|pack)?/gi, ' ');  // "4/1 case" mid-string
  out = out.replace(/^-+\s*/, '');                          // leading dashes ("-SAUCE STEAK...")
  out = out.replace(/\s+/g, ' ').trim();
  // Gentle title-case if the source was SHOUTING
  if (out === out.toUpperCase() && /[A-Z]/.test(out)) {
    out = out.toLowerCase().replace(/\b([a-z])/g, (_, c) => c.toUpperCase());
  }
  return out;
}

function normalizeName(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/s$/, '');
}

function fmtMoney(n) {
  if (n == null || isNaN(n)) return '—';
  return `$${Number(n).toFixed(2)}`;
}

function currentSeason() {
  const m = new Date().getMonth();
  if (m <= 1 || m === 11) return 'winter';
  if (m <= 4) return 'spring';
  if (m <= 7) return 'summer';
  return 'fall';
}

// Strip markdown fences and parse JSON from a Claude response string.
function parseJsonResponse(raw) {
  if (!raw) return null;
  let txt = String(raw).trim();
  txt = txt.replace(/^```json\s*/i, '').replace(/^```\s*/i, '').replace(/```$/i, '').trim();
  try { return JSON.parse(txt); } catch { return null; }
}

async function callMenuCoach({ mode, companyId, system, userMessage }) {
  const r = await fetch('/api/parse-invoice', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: MODEL,
      max_tokens: 2000,
      system,
      messages: [{ role: 'user', content: userMessage }],
      _meta: { feature: 'menu-coach', companyId, mode },
    }),
  });
  if (!r.ok) throw new Error(`API ${r.status}`);
  const data = await r.json();
  const text = data?.content?.[0]?.text || data?.text || '';
  return parseJsonResponse(text);
}

// Grounding: every listed ingredient in a suggestion must resolve to SOMETHING in the
// available set. We used to require an exact normalized-name match, but food_items
// normalized_names are full SKU strings ("GREAT LAKE-CHEESE CHEDDAR FEATHER SHREDDED (case)")
// and the model naturally shortens them when describing a dish ("cheddar cheese"). That
// meant every suggestion got filtered. Now we match token-subset: every word the model
// wrote must appear in some available ingredient's tokens. Still prevents invented items
// (e.g. "gochujang" won't match anything), but allows natural shorthand of real SKUs.
function buildAvailableTokenIndex(availableList) {
  return availableList.map(i => {
    const clean = normalizeName(cleanIngredientName(i.name));
    const tokens = new Set(clean.split(/\s+/).filter(Boolean));
    return { original: i.name, clean, tokens };
  });
}

function matchesAvailable(ingredient, index) {
  const needle = normalizeName(cleanIngredientName(ingredient));
  if (!needle) return false;
  for (const entry of index) {
    if (entry.clean === needle) return true;
  }
  const needleTokens = needle.split(/\s+/).filter(t => t.length >= 3); // ignore filler like 'and', 'of'
  if (needleTokens.length === 0) return false;
  for (const entry of index) {
    if (needleTokens.every(t => entry.tokens.has(t))) return true;
  }
  return false;
}

function groundOrDrop(suggestion, availableIndex) {
  const ings = Array.isArray(suggestion?.ingredients) ? suggestion.ingredients : [];
  const missing = ings.filter(i => !matchesAvailable(i, availableIndex));
  if (missing.length > 0) {
    console.warn('[MenuCoach] filtered ungrounded suggestion', { suggestion, missing });
    return null;
  }
  return suggestion;
}

// ── Main component ──────────────────────────────────────────────────────────────────────────
export default function MenuCoach({ companyId }) {
  const [loading, setLoading] = useState(true);
  const [targetMargin, setTargetMargin] = useState(70);
  const [restaurantType, setRestaurantType] = useState('');
  const [atRiskDishes, setAtRiskDishes] = useState([]); // [{ dish, culpritIngredient, changePct }]
  const [availableIngredients, setAvailableIngredients] = useState([]);
  const [availableIndex, setAvailableIndex] = useState([]);
  const [currentMenuForStyle, setCurrentMenuForStyle] = useState([]);
  const [reactiveResults, setReactiveResults] = useState({}); // { dishId: { status, suggestions, filteredCount } }
  const [savedSuggestions, setSavedSuggestions] = useState([]);
  const [showSpecialsModal, setShowSpecialsModal] = useState(false);
  const [selectedForPrint, setSelectedForPrint] = useState(() => new Set());
  const [printPreview, setPrintPreview] = useState(null); // null | { items, template }

  useEffect(() => { if (companyId) loadAll(); }, [companyId]);

  async function loadAll() {
    setLoading(true);
    try {
      await Promise.all([loadSettings(), loadAvailableIngredients(), loadMenuAndAtRisk(), loadSaved()]);
    } finally {
      setLoading(false);
    }
  }

  async function loadSettings() {
    const [menuRes, companyRes] = await Promise.all([
      supabase.from('menu_settings').select('target_margin_pct').eq('company_id', companyId).maybeSingle(),
      supabase.from('companies').select('restaurant_type').eq('id', companyId).maybeSingle(),
    ]);
    if (menuRes.data?.target_margin_pct != null) setTargetMargin(Number(menuRes.data.target_margin_pct));
    if (companyRes.data?.restaurant_type) setRestaurantType(String(companyRes.data.restaurant_type));
  }

  async function loadAvailableIngredients() {
    // Ingredients purchased in the last 90 days. Uses invoice_line_items joined to food_items
    // so we get the normalized name + current per-unit cost the AI can plausibly price against.
    const ninety = new Date(Date.now() - 90 * 24 * 3600 * 1000).toISOString().slice(0, 10);
    const { data } = await supabase
      .from('invoice_line_items')
      .select('food_item_id, food_items!inner(id, normalized_name, current_price, unit, price_per_lb)')
      .eq('company_id', companyId)
      .gte('invoice_date', ninety);
    const byId = new Map();
    (data || []).forEach(r => {
      const fi = r.food_items;
      if (!fi || !fi.normalized_name) return;
      if (!byId.has(fi.id)) {
        byId.set(fi.id, {
          name: fi.normalized_name,
          unit: fi.unit || null,
          unit_cost: Number(fi.current_price) || null,
          price_per_lb: Number(fi.price_per_lb) || null,
        });
      }
    });
    const list = [...byId.values()];
    setAvailableIngredients(list);
    setAvailableIndex(buildAvailableTokenIndex(list));
  }

  async function loadMenuAndAtRisk() {
    // Pull all active menu items + their recipe ingredients + recent price change per ingredient.
    // At-risk = margin_pct < target_margin_pct AND at least one ingredient has price_change_pct > 0.
    const [menuRes, recipeRes] = await Promise.all([
      supabase.from('menu_items').select('id, name, category, menu_price, plate_cost, margin_pct').eq('company_id', companyId).eq('is_active', true),
      supabase.from('recipe_ingredients').select('menu_item_id, food_item_id, ingredient_name, quantity, unit, line_cost').eq('company_id', companyId),
    ]);
    const items = menuRes.data || [];
    const recipes = recipeRes.data || [];

    // Keep a short list for style reference in the specials prompt.
    setCurrentMenuForStyle(
      items.slice(0, 15).map(m => ({ name: m.name, category: m.category, price: Number(m.menu_price) || 0 }))
    );

    // Attach recent price-change % per ingredient so we can name the culprit.
    const foodItemIds = [...new Set(recipes.map(r => r.food_item_id).filter(Boolean))];
    let priceChangeById = {};
    if (foodItemIds.length > 0) {
      const { data: fiData } = await supabase
        .from('food_items')
        .select('id, normalized_name, price_change_pct, current_price')
        .in('id', foodItemIds);
      (fiData || []).forEach(f => { priceChangeById[f.id] = f; });
    }

    const atRisk = [];
    items.forEach(dish => {
      const margin = Number(dish.margin_pct);
      if (!Number.isFinite(margin)) return;
      if (margin >= targetMargin) return;
      const ings = recipes.filter(r => String(r.menu_item_id) === String(dish.id));
      // Prefer to name a culprit: biggest positive recent price change. Fall back to the
      // priciest ingredient if no recent spike is recorded — tells the AI which ingredient
      // to work around even when price history is thin.
      let culprit = null;
      ings.forEach(ing => {
        const fi = priceChangeById[ing.food_item_id];
        const pct = Number(fi?.price_change_pct) || 0;
        if (pct > 0 && (!culprit || pct > culprit.changePct)) {
          culprit = {
            ingredient_name: fi?.normalized_name || ing.ingredient_name,
            changePct: pct,
            unit_cost: Number(fi?.current_price) || null,
            reason: 'recent_price_increase',
          };
        }
      });
      if (!culprit) {
        let priciest = null;
        ings.forEach(ing => {
          const cost = Number(ing.line_cost) || 0;
          if (cost > 0 && (!priciest || cost > priciest.line_cost)) {
            priciest = { ...ing, line_cost: cost };
          }
        });
        if (priciest) {
          culprit = {
            ingredient_name: priciest.ingredient_name,
            changePct: 0,
            unit_cost: null,
            reason: 'highest_cost_component',
          };
        }
      }
      // Include the dish even if we can't identify any ingredient at all — margin is what matters.
      atRisk.push({ dish, culprit, ingredientCount: ings.length });
    });
    // Sort lowest margin first so the worst offenders render at the top.
    atRisk.sort((a, b) => Number(a.dish.margin_pct) - Number(b.dish.margin_pct));
    setAtRiskDishes(atRisk);
  }

  async function loadSaved() {
    const { data } = await supabase
      .from('menu_coach_suggestions')
      .select('*')
      .eq('company_id', companyId)
      .in('status', ['saved', 'edited'])
      .order('created_at', { ascending: false })
      .limit(20);
    setSavedSuggestions(data || []);
  }

  // ── Reactive: generate suggestions for one at-risk dish ────────────────────────────────────
  async function generateForDish(entry) {
    const key = entry.dish.id;
    setReactiveResults(prev => ({ ...prev, [key]: { status: 'loading' } }));
    try {
      const payload = {
        restaurant_type: restaurantType || 'unspecified',
        flagged_ingredient: entry.culprit
          ? {
              name: entry.culprit.ingredient_name,
              percent_increase_30d: entry.culprit.changePct,
              note: entry.culprit.reason === 'recent_price_increase'
                ? 'Ingredient spiked recently.'
                : 'No recent price spike recorded — named as the priciest component of this dish.',
            }
          : { note: 'No ingredient data linked to this dish — margin is structurally below target.' },
        affected_dish: {
          name: entry.dish.name,
          category: entry.dish.category,
          menu_price: Number(entry.dish.menu_price) || 0,
          plate_cost: Number(entry.dish.plate_cost) || 0,
          current_margin_pct: Number(entry.dish.margin_pct) || 0,
          target_margin_pct: targetMargin,
        },
        available_ingredients: availableIngredients.map(i => ({
          name: i.name, unit: i.unit, approximate_unit_cost: i.unit_cost,
        })),
      };
      const parsed = await callMenuCoach({
        mode: 'reactive',
        companyId,
        system: REACTIVE_SYSTEM,
        userMessage: `Please suggest alternative dishes. INPUT:\n\n${JSON.stringify(payload, null, 2)}`,
      });
      const raw = Array.isArray(parsed?.suggestions) ? parsed.suggestions : [];
      const grounded = raw.map(s => groundOrDrop(s, availableIndex)).filter(Boolean);
      setReactiveResults(prev => ({
        ...prev,
        [key]: { status: 'done', suggestions: grounded, filteredCount: raw.length - grounded.length },
      }));
    } catch (e) {
      console.error('[MenuCoach] reactive generation failed', e);
      setReactiveResults(prev => ({ ...prev, [key]: { status: 'error' } }));
    }
  }

  async function saveReactiveSuggestion(entry, suggestion) {
    await supabase.from('menu_coach_suggestions').insert([{
      company_id: companyId,
      type: 'reactive',
      trigger_data: { dish_id: entry.dish.id, dish_name: entry.dish.name, culprit: entry.culprit },
      generated_content: suggestion,
      status: 'saved',
    }]);
    loadSaved();
  }

  function dismissReactiveSuggestion(entryKey, index) {
    setReactiveResults(prev => {
      const cur = prev[entryKey];
      if (!cur) return prev;
      const next = cur.suggestions.filter((_, i) => i !== index);
      return { ...prev, [entryKey]: { ...cur, suggestions: next } };
    });
  }

  // ── Render ─────────────────────────────────────────────────────────────────────────────────
  return (
    <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>Menu Coach</h1>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
              AI suggestions for at-risk dishes and on-demand specials
              {restaurantType && <span className="ml-2">· tailored for <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{restaurantType.replace(/_/g, ' ')}</span></span>}
              {!restaurantType && <span className="ml-2" style={{ color: 'var(--text-warning)' }}>· set your restaurant type in Settings for format-aware suggestions</span>}
            </p>
          </div>
          <button
            onClick={() => setShowSpecialsModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ background: 'var(--accent-primary)' }}>
            <SparkleIcon />
            Generate Specials
          </button>
        </div>

        {/* Phase 1: price-triggered section */}
        <section className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-sm font-semibold text-gray-900">At-risk dishes</h2>
            <span className="text-xs text-gray-400">below {targetMargin}% margin target</span>
          </div>

          {loading ? (
            <SkeletonRow />
          ) : atRiskDishes.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
              <div className="text-sm text-gray-600 mb-1 font-medium">No margin-eroding price spikes detected</div>
              <div className="text-xs text-gray-400">Every tracked dish is above the {targetMargin}% target — your margins are stable.</div>
            </div>
          ) : (
            <div className="space-y-4">
              {atRiskDishes.map(entry => {
                const key = entry.dish.id;
                const r = reactiveResults[key] || { status: 'idle' };
                return (
                  <div key={key} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <div className="flex items-start justify-between gap-3 p-4 border-b border-gray-100">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-900 truncate">{entry.dish.name}</div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                          <span>{entry.dish.category || 'Uncategorized'}</span>
                          <span>·</span>
                          <span>Margin <span className="text-red-600 font-semibold">{Number(entry.dish.margin_pct).toFixed(1)}%</span> / target {targetMargin}%</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {entry.culprit ? (
                            entry.culprit.reason === 'recent_price_increase' ? (
                              <>
                                Culprit: <span className="font-medium text-gray-700">{entry.culprit.ingredient_name}</span>
                                <span className="ml-1 text-amber-700 font-semibold">+{entry.culprit.changePct.toFixed(1)}%</span>
                              </>
                            ) : (
                              <>
                                Most expensive ingredient: <span className="font-medium text-gray-700">{entry.culprit.ingredient_name}</span>
                                <span className="ml-1 text-gray-400">(no recent price change on record)</span>
                              </>
                            )
                          ) : (
                            <span className="text-gray-400 italic">Structural margin issue — no ingredient data linked.</span>
                          )}
                        </div>
                      </div>
                      {r.status !== 'done' && r.status !== 'loading' && (
                        <button onClick={() => generateForDish(entry)} className="px-3 py-1.5 rounded-lg text-xs font-medium text-white" style={{ background: 'var(--accent-primary)' }}>
                          Get suggestions
                        </button>
                      )}
                    </div>

                    {r.status === 'loading' && <SkeletonCards />}
                    {r.status === 'error' && (
                      <div className="p-4 text-xs text-red-600">Couldn't generate suggestions right now — try again in a moment.</div>
                    )}
                    {r.status === 'done' && r.suggestions.length === 0 && (
                      <div className="p-4 text-xs text-gray-500 italic">
                        We couldn't generate grounded suggestions from your current inventory. Try uploading more recent invoices.
                        {r.filteredCount > 0 && <> ({r.filteredCount} filtered for ungrounded ingredients.)</>}
                      </div>
                    )}
                    {r.status === 'done' && r.suggestions.length > 0 && (
                      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                        {r.suggestions.map((s, i) => (
                          <SuggestionCard
                            key={i}
                            title={s.dishName}
                            description={s.description}
                            ingredients={s.ingredients}
                            costLabel="Food cost"
                            cost={s.estimatedFoodCost}
                            margin={s.estimatedMargin}
                            reasoning={s.reasoning}
                            onSave={() => { saveReactiveSuggestion(entry, s); dismissReactiveSuggestion(key, i); }}
                            onDismiss={() => dismissReactiveSuggestion(key, i)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Saved history */}
        {savedSuggestions.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <h2 className="text-sm font-semibold text-gray-900">Saved ideas</h2>
              <div className="flex items-center gap-2">
                {selectedForPrint.size > 0 && (
                  <>
                    <button onClick={() => setSelectedForPrint(new Set())} className="text-xs px-2 py-1 rounded-md text-gray-500 hover:bg-gray-100">Clear</button>
                    <button
                      onClick={() => {
                        const items = savedSuggestions.filter(s => selectedForPrint.has(s.id));
                        setPrintPreview({ items, template: 'elegant' });
                      }}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-semibold text-white"
                      style={{ background: 'var(--accent-primary)' }}>
                      <PrinterIcon />
                      Print selected ({selectedForPrint.size})
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="space-y-2">
              {savedSuggestions.map(s => {
                const content = s.generated_content || {};
                const isChecked = selectedForPrint.has(s.id);
                return (
                  <div key={s.id} className={`group bg-white border rounded-lg p-3 flex items-start gap-3 transition-colors ${isChecked ? 'border-indigo-300 bg-indigo-50/40' : 'border-gray-200'}`}>
                    <label className="flex items-center pt-0.5 cursor-pointer">
                      <input type="checkbox" checked={isChecked}
                        onChange={() => {
                          setSelectedForPrint(prev => {
                            const next = new Set(prev);
                            if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                            return next;
                          });
                        }}
                        className="cursor-pointer" />
                    </label>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-gray-900 truncate">{content.dishName || content.name || 'Untitled idea'}</div>
                      <div className="text-xs text-gray-500 truncate">{content.description}</div>
                      <div className="text-[10px] text-gray-400 mt-0.5 uppercase tracking-wide">
                        {s.type === 'reactive' ? 'reactive' : 'on-demand'} · {new Date(s.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-xs text-green-600 font-semibold whitespace-nowrap">
                        {fmtMoney(content.estimatedMargin ?? content.margin ?? null)} margin
                      </div>
                      <button
                        onClick={() => setPrintPreview({ items: [s], template: 'elegant' })}
                        className="text-gray-300 hover:text-indigo-600 transition-colors opacity-0 group-hover:opacity-100"
                        title="Print this card">
                        <PrinterIcon />
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm('Delete this saved idea?')) return;
                          await supabase.from('menu_coach_suggestions').delete().eq('id', s.id);
                          setSelectedForPrint(prev => { const n = new Set(prev); n.delete(s.id); return n; });
                          loadSaved();
                        }}
                        className="text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                        title="Delete">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>

      {showSpecialsModal && (
        <SpecialsModal
          companyId={companyId}
          restaurantType={restaurantType}
          availableIngredients={availableIngredients}
          availableIndex={availableIndex}
          currentMenuForStyle={currentMenuForStyle}
          onClose={() => setShowSpecialsModal(false)}
          onSaved={loadSaved}
        />
      )}

      {printPreview && (
        <PrintPreviewModal
          items={printPreview.items}
          template={printPreview.template}
          onTemplateChange={t => setPrintPreview(p => ({ ...p, template: t }))}
          onClose={() => setPrintPreview(null)}
        />
      )}
    </div>
  );
}

// ── Print preview modal ────────────────────────────────────────────────────────────────────
function PrintPreviewModal({ items, template, onTemplateChange, onClose }) {
  const TEMPLATES = [
    { key: 'elegant', label: 'Elegant Dark' },
    { key: 'bright',  label: 'Bright Modern' },
    { key: 'minimal', label: 'Minimal' },
  ];
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto print-preview-backdrop" style={{ background: 'rgba(0,0,0,0.55)' }} onClick={onClose}>
      <style dangerouslySetInnerHTML={{ __html: `
        @media print {
          body * { visibility: hidden !important; }
          .print-preview-backdrop { position: static !important; padding: 0 !important; background: #FFF !important; display: block !important; }
          .print-preview-backdrop, .print-preview-backdrop * { visibility: visible !important; }
          .print-preview-chrome { display: none !important; }
          .print-card-sheet { position: absolute; left: 0; top: 0; right: 0; margin: 0 !important; padding: 0.35in !important; width: 100%; box-shadow: none !important; border: none !important; background: #FFF !important; }
          .print-card { break-inside: avoid; page-break-inside: avoid; box-shadow: none !important; }
          @page { size: letter; margin: 0.35in; }
        }
      ` }} />
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl border border-gray-200 w-full max-w-4xl my-6">
        <div className="print-preview-chrome flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-base font-semibold text-gray-900">Print preview</div>
            <div className="flex gap-0.5 rounded-lg p-0.5" style={{ background: 'var(--bg-hover)' }}>
              {TEMPLATES.map(t => (
                <button key={t.key} onClick={() => onTemplateChange(t.key)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium ${template === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100">Cancel</button>
            <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-semibold text-white" style={{ background: 'var(--accent-primary)' }}>
              <PrinterIcon /> Print
            </button>
          </div>
        </div>
        <div className="p-5 bg-gray-50 max-h-[70vh] overflow-y-auto print-preview-chrome-off">
          <div className="print-card-sheet grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
            {items.map(s => <PrintCard key={s.id} item={s} template={template} />)}
          </div>
          <div className="print-preview-chrome text-[11px] text-gray-400 text-center mt-4">Tip: cards are sized ~3.5″ × 4.5″. After printing, cut and fold to make table tents.</div>
        </div>
      </div>
    </div>
  );
}

function PrintCard({ item, template }) {
  const c = item.generated_content || {};
  const title = c.dishName || c.name || 'Special';
  const description = c.description || '';
  const ingredients = Array.isArray(c.ingredients) ? c.ingredients : [];
  const price = c.suggestedPrice ?? c.menu_price ?? null;

  // Template styling. Each template returns its own classed/inline treatment.
  const styles = {
    elegant: {
      cardStyle: { background: '#1F2937', color: '#F9FAFB', border: '1px solid #111827' },
      label: { color: '#A78BFA', letterSpacing: '0.2em' },
      title: { color: '#F9FAFB', fontFamily: 'Georgia, serif' },
      desc: { color: '#D1D5DB' },
      chip: { background: 'rgba(255,255,255,0.08)', color: '#E5E7EB', border: '1px solid rgba(255,255,255,0.12)' },
      priceColor: '#FBBF24',
    },
    bright: {
      cardStyle: { background: 'linear-gradient(135deg, #FEF3C7, #FBD38D)', color: '#78350F', border: '1px solid #FBBF24' },
      label: { color: '#B45309', letterSpacing: '0.2em' },
      title: { color: '#7C2D12' },
      desc: { color: '#78350F' },
      chip: { background: 'rgba(255,255,255,0.55)', color: '#78350F', border: '1px solid rgba(180,83,9,0.25)' },
      priceColor: '#7C2D12',
    },
    minimal: {
      cardStyle: { background: '#FFFFFF', color: '#111827', border: '1px solid #E5E7EB' },
      label: { color: '#6B7280', letterSpacing: '0.2em' },
      title: { color: '#111827' },
      desc: { color: '#4B5563' },
      chip: { background: '#F3F4F6', color: '#374151', border: '1px solid #E5E7EB' },
      priceColor: '#111827',
    },
  };
  const t = styles[template] || styles.elegant;

  return (
    <div className="print-card rounded-xl p-5 flex flex-col" style={{ ...t.cardStyle, minHeight: 360, aspectRatio: '3.5 / 4.5', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>
      <div className="text-[10px] font-bold uppercase" style={t.label}>
        {item.trigger_data?.category === 'bar' ? 'Drink Special' : item.trigger_data?.category === 'food' ? 'Food Special' : "Today's Special"}
      </div>
      <div className="text-xl font-bold leading-tight mt-2" style={t.title}>{title}</div>
      {description && <div className="text-xs mt-2 leading-snug" style={t.desc}>{description}</div>}
      {ingredients.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-3">
          {ingredients.slice(0, 6).map((ing, i) => (
            <span key={i} className="text-[9px] px-2 py-0.5 rounded-full font-medium" style={t.chip}>{cleanIngredientName(ing)}</span>
          ))}
        </div>
      )}
      <div className="flex-1" />
      {price != null && (
        <div className="mt-3 text-right">
          <span className="text-2xl font-bold" style={{ color: t.priceColor, fontFamily: 'Georgia, serif' }}>{fmtMoney(price)}</span>
        </div>
      )}
    </div>
  );
}

function PrinterIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="6 9 6 2 18 2 18 9"/>
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
      <rect x="6" y="14" width="12" height="8"/>
    </svg>
  );
}

// ── Suggestion card ─────────────────────────────────────────────────────────────────────────
function SuggestionCard({ title, description, ingredients, costLabel = 'Cost', cost, price, margin, reasoning, onSave, onDismiss, onEdit }) {
  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-semibold text-gray-900">{title}</div>
        {margin != null && (
          <div className="text-sm font-bold text-green-600 whitespace-nowrap">{fmtMoney(margin)}</div>
        )}
      </div>
      {description && <div className="text-xs text-gray-600 mt-1">{description}</div>}
      {Array.isArray(ingredients) && ingredients.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {ingredients.map((ing, i) => (
            <span key={i} className="text-[10px] text-gray-600 bg-white border border-gray-200 rounded-full px-2 py-0.5">{cleanIngredientName(ing)}</span>
          ))}
        </div>
      )}
      <div className="flex items-center gap-3 mt-2 text-[11px] text-gray-500">
        {cost != null && <span>{costLabel}: <span className="text-gray-700 font-medium">{fmtMoney(cost)}</span></span>}
        {price != null && <span>Price: <span className="text-gray-700 font-medium">{fmtMoney(price)}</span></span>}
      </div>
      {reasoning && <div className="text-[11px] text-gray-500 italic mt-2">{reasoning}</div>}
      <div className="flex items-center justify-end gap-2 mt-3">
        {onEdit && (
          <button onClick={onEdit} className="text-xs px-2 py-1 rounded-md text-gray-600 hover:bg-gray-100">Edit</button>
        )}
        <button onClick={onDismiss} className="text-xs px-2 py-1 rounded-md text-gray-500 hover:bg-gray-100">Dismiss</button>
        <button onClick={onSave} className="text-xs px-2.5 py-1 rounded-md font-semibold transition-colors" style={{ background: 'var(--text-primary)', color: 'var(--bg-card)', border: 'none', cursor: 'pointer' }} onMouseEnter={e => (e.currentTarget.style.background = '#262626')} onMouseLeave={e => (e.currentTarget.style.background = 'var(--text-primary)')}>
          {onEdit ? 'Save to Specials' : 'Save to menu ideas'}
        </button>
      </div>
    </div>
  );
}

// ── Specials modal (Phase 2) ────────────────────────────────────────────────────────────────
function SpecialsModal({ companyId, restaurantType, availableIngredients, availableIndex, currentMenuForStyle, onClose, onSaved }) {
  const [prompt, setPrompt] = useState('');
  const [category, setCategory] = useState('both');
  const [occasion, setOccasion] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null); // grounded specials array
  const [filteredCount, setFilteredCount] = useState(0);
  const [editingIdx, setEditingIdx] = useState(null);

  async function generate() {
    setLoading(true); setError(null); setResults(null); setFilteredCount(0);
    try {
      const payload = {
        restaurant_type: restaurantType || 'unspecified',
        user_request: prompt.trim(),
        category,
        occasion: occasion.trim() || null,
        current_date: new Date().toISOString().slice(0, 10),
        season: currentSeason(),
        current_menu: currentMenuForStyle,
        available_ingredients: availableIngredients.map(i => ({
          name: i.name, unit: i.unit, approximate_unit_cost: i.unit_cost,
        })),
      };
      const parsed = await callMenuCoach({
        mode: 'ondemand',
        companyId,
        system: ONDEMAND_SYSTEM,
        userMessage: `Please generate specials. INPUT:\n\n${JSON.stringify(payload, null, 2)}`,
      });
      const raw = Array.isArray(parsed?.specials) ? parsed.specials : [];
      const grounded = raw.map(s => groundOrDrop(s, availableIndex)).filter(Boolean);
      setResults(grounded);
      setFilteredCount(raw.length - grounded.length);
    } catch (e) {
      console.error('[MenuCoach] specials generation failed', e);
      setError("Couldn't generate specials right now — try again in a moment.");
    } finally {
      setLoading(false);
    }
  }

  async function saveSpecial(idx) {
    const s = results[idx];
    await supabase.from('menu_coach_suggestions').insert([{
      company_id: companyId,
      type: 'ondemand',
      user_prompt: prompt.trim(),
      trigger_data: { category, occasion: occasion || null },
      generated_content: s,
      status: 'saved',
    }]);
    setResults(prev => prev.filter((_, i) => i !== idx));
    onSaved?.();
  }

  function discardSpecial(idx) {
    setResults(prev => prev.filter((_, i) => i !== idx));
  }

  function updateSpecial(idx, patch) {
    setResults(prev => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl border border-gray-200 w-full max-w-2xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <SparkleIcon />
            <div className="text-base font-semibold text-gray-900">Generate Specials</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg">&times;</button>
        </div>
        <div className="p-5">
          {!results && (
            <>
              <div className="mb-4">
                <label className="text-xs font-medium text-gray-500 block mb-1.5">What kind of specials do you want?</label>
                <textarea
                  value={prompt} onChange={e => setPrompt(e.target.value)}
                  rows={3}
                  placeholder="5 cocktail specials for game day Sunday"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-gray-50 resize-none"
                />
              </div>
              <div className="mb-4">
                <label className="text-xs font-medium text-gray-500 block mb-1.5">Category</label>
                <div className="flex gap-0.5 rounded-lg p-0.5 w-fit" style={{ background: 'var(--bg-hover)' }}>
                  {[
                    { key: 'food', label: 'Food' },
                    { key: 'bar', label: 'Bar' },
                    { key: 'both', label: 'Both' },
                  ].map(c => (
                    <button key={c.key} onClick={() => setCategory(c.key)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${category === c.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mb-5">
                <label className="text-xs font-medium text-gray-500 block mb-1.5">Occasion or event (optional)</label>
                <input
                  value={occasion} onChange={e => setOccasion(e.target.value)}
                  placeholder="Mother's Day brunch, Bills home game, etc."
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400 bg-gray-50"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100">Cancel</button>
                <button onClick={generate} disabled={loading || !prompt.trim() || availableIngredients.length === 0}
                  className="px-4 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-40" style={{ background: 'var(--accent-primary)' }}>
                  {loading ? 'Generating…' : 'Generate 5 specials'}
                </button>
              </div>
              {availableIngredients.length === 0 && (
                <div className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  No invoices in the last 90 days — the AI has no ingredients to ground against. Upload recent invoices first.
                </div>
              )}
            </>
          )}

          {loading && !results && <SkeletonCards count={5} />}
          {error && <div className="text-xs text-red-600">{error}</div>}

          {results && (
            <>
              <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold text-gray-900">{results.length} grounded {results.length === 1 ? 'special' : 'specials'}</div>
                {filteredCount > 0 && (
                  <div className="text-[11px] text-amber-700">Filtered {filteredCount} with ungrounded ingredients.</div>
                )}
              </div>
              {results.length === 0 ? (
                <div className="text-xs text-gray-500 italic">
                  We couldn't generate grounded suggestions from your current inventory. Try uploading more recent invoices.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {results.map((s, i) => (
                    editingIdx === i ? (
                      <EditSpecialForm key={i} special={s} onSave={patch => { updateSpecial(i, patch); setEditingIdx(null); }} onCancel={() => setEditingIdx(null)} />
                    ) : (
                      <SuggestionCard
                        key={i}
                        title={s.name}
                        description={s.description}
                        ingredients={s.ingredients}
                        costLabel="Cost"
                        cost={s.estimatedCost}
                        price={s.suggestedPrice}
                        margin={s.estimatedMargin}
                        reasoning={s.reasoning}
                        onSave={() => saveSpecial(i)}
                        onDismiss={() => discardSpecial(i)}
                        onEdit={() => setEditingIdx(i)}
                      />
                    )
                  ))}
                </div>
              )}
              <div className="flex items-center justify-end gap-2 mt-5">
                <button onClick={() => { setResults(null); setFilteredCount(0); }} className="text-xs px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100">New request</button>
                <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-lg font-semibold text-white" style={{ background: 'var(--accent-primary)' }}>Done</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function EditSpecialForm({ special, onSave, onCancel }) {
  const [draft, setDraft] = useState({
    name: special.name || '',
    description: special.description || '',
    ingredients: (special.ingredients || []).join(', '),
    suggestedPrice: special.suggestedPrice ?? 0,
    estimatedCost: special.estimatedCost ?? 0,
  });
  const margin = (Number(draft.suggestedPrice) || 0) - (Number(draft.estimatedCost) || 0);
  return (
    <div className="border border-blue-200 rounded-lg p-3 bg-blue-50 col-span-full md:col-span-1">
      <div className="text-xs font-semibold text-blue-900 mb-2">Editing</div>
      <div className="flex flex-col gap-2">
        <input value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} placeholder="Name"
          className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-blue-400 bg-white" />
        <textarea value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })} rows={2}
          className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-blue-400 bg-white resize-none" />
        <input value={draft.ingredients} onChange={e => setDraft({ ...draft, ingredients: e.target.value })} placeholder="Ingredients (comma separated)"
          className="w-full border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-blue-400 bg-white" />
        <div className="flex items-center gap-2">
          <label className="text-[11px] text-gray-500">Price</label>
          <input type="number" step="0.25" value={draft.suggestedPrice} onChange={e => setDraft({ ...draft, suggestedPrice: e.target.value })}
            className="w-20 border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-blue-400 bg-white" />
          <label className="text-[11px] text-gray-500 ml-2">Cost</label>
          <input type="number" step="0.25" value={draft.estimatedCost} onChange={e => setDraft({ ...draft, estimatedCost: e.target.value })}
            className="w-20 border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-blue-400 bg-white" />
          <span className="text-[11px] text-green-700 font-semibold ml-auto">Margin {fmtMoney(margin)}</span>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 mt-3">
        <button onClick={onCancel} className="text-xs px-2 py-1 rounded-md text-gray-600 hover:bg-white">Cancel</button>
        <button
          onClick={() => onSave({
            name: draft.name.trim(),
            description: draft.description.trim(),
            ingredients: draft.ingredients.split(',').map(s => s.trim()).filter(Boolean),
            suggestedPrice: Number(draft.suggestedPrice) || 0,
            estimatedCost: Number(draft.estimatedCost) || 0,
            estimatedMargin: margin,
          })}
          className="text-xs px-2.5 py-1 rounded-md font-semibold text-white" style={{ background: 'var(--accent-primary)' }}>
          Apply edits
        </button>
      </div>
    </div>
  );
}

// ── Tiny icons + skeletons ──────────────────────────────────────────────────────────────────
function SparkleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l1.8 4.5L18 9l-4.2 1.5L12 15l-1.8-4.5L6 9l4.2-1.5L12 3z"/>
      <path d="M19 13l.9 2.2L22 16l-2.1.8L19 19l-.9-2.2L16 16l2.1-.8L19 13z"/>
    </svg>
  );
}

function SkeletonRow() {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 animate-pulse">
      <div className="h-4 w-40 bg-gray-100 rounded mb-2" />
      <div className="h-3 w-64 bg-gray-100 rounded" />
    </div>
  );
}

function SkeletonCards({ count = 3 }) {
  return (
    <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="border border-gray-200 rounded-lg p-3 bg-gray-50 animate-pulse">
          <div className="h-4 w-36 bg-gray-200 rounded mb-2" />
          <div className="h-3 w-full bg-gray-200 rounded mb-1" />
          <div className="h-3 w-3/4 bg-gray-200 rounded mb-3" />
          <div className="flex gap-1">
            {Array.from({ length: 4 }).map((__, j) => <div key={j} className="h-4 w-14 bg-gray-200 rounded-full" />)}
          </div>
        </div>
      ))}
    </div>
  );
}
